import React, { useState, useEffect } from 'react'
import { X, BarChart3, Table, Calendar } from 'lucide-react'
import Chart from 'react-apexcharts'
import { soundchartsEndpoints } from '../../repositories/soundcharts'
import { Skeleton, SkeletonText } from '../ui/skeleton'
import Button from '../ui/Button'
import { colors } from '../../theme/tokens'

interface HistoricalTrendModalProps {
    isOpen: boolean
    onClose: () => void
    title: string
    dataType?: 'artist' | 'song'
    songUuid?: string | null
    artistUuid?: string | null
}

const HistoricalTrendModal: React.FC<HistoricalTrendModalProps> = ({
    isOpen,
    onClose,
    title,
    dataType = 'song',
    songUuid,
    artistUuid
}) => {
    const [viewMode, setViewMode] = useState<'chart' | 'table'>('chart')
    const [data, setData] = useState<any[]>([])
    const [loading, setLoading] = useState(false)
    const [error, setError] = useState<string | null>(null)

    // Date range states
    const [dateRange, setDateRange] = useState<'60' | '90' | 'custom'>('90')
    const [customStartDate, setCustomStartDate] = useState<string>('')
    const [customEndDate, setCustomEndDate] = useState<string>('')

    // Get today's date in YYYY-MM-DD format
    const getTodayDate = () => {
        const today = new Date()
        return today.toISOString().split('T')[0]
    }

    // Calculate date range
    const getDateRange = () => {
        const endDate = new Date()
        const startDate = new Date()

        if (dateRange === '60') {
            startDate.setDate(endDate.getDate() - 60)
        } else if (dateRange === '90') {
            startDate.setDate(endDate.getDate() - 90)
        } else if (dateRange === 'custom' && customStartDate && customEndDate) {
            return {
                startDate: customStartDate,
                endDate: customEndDate
            }
        }

        return {
            startDate: startDate.toISOString().split('T')[0],
            endDate: endDate.toISOString().split('T')[0]
        }
    }

    // Fetch data when modal opens and UUID/dataType/dateRange changes
    useEffect(() => {
        if (!isOpen) return

        const fetchPopularityData = async () => {
            // FIX 1: Validate custom date range before fetching
            if (dateRange === 'custom' && (!customStartDate || !customEndDate)) {
                return
            }

            // FIX 2: Validate UUIDs before fetching
            if (dataType === 'song' && !songUuid) {
                setError('Song UUID is missing')
                return
            }
            if (dataType === 'artist' && !artistUuid) {
                setError('Artist UUID is missing')
                return
            }

            setLoading(true)
            setError(null)

            try {
                const { startDate, endDate } = getDateRange()
                const query = `startDate=${startDate}&endDate=${endDate}`

                let response

                if (dataType === 'song' && songUuid) {
                    response = await soundchartsEndpoints.getPopularityBySong(songUuid, "spotify", query)
                    setData(response?.data?.items || [])
                } else if (dataType === 'artist' && artistUuid) {
                    response = await soundchartsEndpoints.getPopularityByArtist(artistUuid, "spotify", query)
                    setData(response?.data?.items || [])
                } else {
                    setData([])
                }
            } catch (err: any) {
                console.error('Failed to fetch popularity data:', err)
                setError(err?.message || 'Failed to load popularity data')
                setData([])
            } finally {
                setLoading(false)
            }
        }

        fetchPopularityData()
    }, [isOpen, dataType, songUuid, artistUuid, dateRange, customStartDate, customEndDate])

    // Validate custom date range
    const handleCustomEndDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const selectedDate = e.target.value

        // If start date is set, end date should be after start date
        if (customStartDate && selectedDate < customStartDate) {
            setError('End date must be after start date')
            return
        }

        setCustomEndDate(selectedDate)
        setError(null)
    }

    const handleCustomStartDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const selectedDate = e.target.value
        const today = getTodayDate()

        // Don't allow future dates
        if (selectedDate > today) {
            return
        }

        setCustomStartDate(selectedDate)
        setError(null)
    }

    // Reset custom dates when switching to preset ranges
    const handleDateRangeChange = (range: '60' | '90' | 'custom') => {
        setDateRange(range)
        if (range !== 'custom') {
            setCustomStartDate('')
            setCustomEndDate('')
            setError(null)
        }
    }

    if (!isOpen) return null

    // Check if data has plots (song data) or direct values (artist data)
    const hasPlots = data.length > 0 && data[0]?.plots

    // Prepare chart data
    const prepareChartData = () => {
        let chartData: { x: string; y: number }[] = []

        if (hasPlots) {
            data.forEach(item => {
                if (item.plots && item.plots.length > 0) {
                    // Get the maximum popularity value from all song versions
                    const maxValue = Math.max(...item.plots.map((plot: any) => plot.value || 0))

                    chartData.push({
                        x: new Date(item.date).toLocaleDateString(),
                        y: maxValue
                    })
                }
            })
        } else {
            // Artist data - direct value without plots
            chartData = data
                .filter(item => item.date && item.value !== undefined)
                .map(item => ({
                    x: new Date(item.date).toLocaleDateString(),
                    y: item.value
                }))
        }

        return dataType === 'song' ? chartData.reverse() : chartData
    }

    // Prepare table data
    const prepareTableData = () => {
        if (hasPlots) {
            return [...data].sort((a, b) => {
                const dateA = new Date(a.date).getTime()
                const dateB = new Date(b.date).getTime()
                return dateB - dateA
            })
        } else {
            return [...data].sort((a, b) => {
                const dateA = new Date(a.date).getTime()
                const dateB = new Date(b.date).getTime()
                return dateB - dateA
            })
        }
    }

    const chartOptions = {
        chart: {
            type: 'line' as const,
            height: 400,
            zoom: {
                enabled: true
            },
            toolbar: {
                show: true,
                tools: {
                    download: true,
                    selection: true,
                    zoom: true,
                    zoomin: true,
                    zoomout: true,
                    pan: true,
                    reset: true
                }
            }
        },
        dataLabels: {
            enabled: false
        },
        stroke: {
            curve: 'smooth' as const,
            width: 3
        },
        xaxis: {
            type: 'category' as const,
            title: {
                text: 'Date',
                style: {
                    fontSize: '14px',
                    fontWeight: 600
                }
            },
            labels: {
                rotate: -45,
                rotateAlways: true,
                hideOverlappingLabels: true,
                trim: false,
                style: {
                    fontSize: '11px'
                },
                formatter: function (value: string) {
                    // Show fewer labels to avoid overlap
                    return value
                }
            },
            tickAmount: 10
        },
        yaxis: {
            title: {
                text: 'Popularity Score',
                style: {
                    fontSize: '14px',
                    fontWeight: 600
                }
            },
            labels: {
                style: {
                    fontSize: '12px'
                }
            }
        },
        tooltip: {
            x: {
                format: 'dd MMM yyyy'
            },
            y: {
                formatter: function (value: number) {
                    return value.toFixed(0)
                }
            }
        },
        colors: [colors.primary[700]],
        grid: {
            borderColor: colors.neutral[200],
            strokeDashArray: 4
        }
    }

    const chartSeries = [
        {
            name: 'Popularity',
            data: prepareChartData()
        }
    ]

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
            <div className="w-full max-w-7xl max-h-[95vh] bg-white rounded-lg shadow-xl overflow-hidden flex flex-col">
                {/* Modal Header */}
                <div className="flex items-center justify-between p-6 border-b border-neutral-200 bg-white">
                    <h2 className="text-lg font-semibold text-black">{title}</h2>

                    <div className="flex items-center gap-4">
                        {/* View Toggle Button */}
                        <div className="flex bg-neutral-100 rounded-lg p-1">
                            <button
                                onClick={() => setViewMode('chart')}
                                className={`flex items-center gap-2 px-3 py-2 rounded-md text-sm font-medium transition-colors ${viewMode === 'chart'
                                    ? 'bg-white text-primary-700 shadow-sm'
                                    : 'text-neutral-500 hover:text-neutral-700'
                                    }`}
                            >
                                <BarChart3 size={16} />
                                Chart
                            </button>
                            <button
                                onClick={() => setViewMode('table')}
                                className={`flex items-center gap-2 px-3 py-2 rounded-md text-sm font-medium transition-colors ${viewMode === 'table'
                                    ? 'bg-white text-primary-700 shadow-sm'
                                    : 'text-neutral-500 hover:text-neutral-700'
                                    }`}
                            >
                                <Table size={16} />
                                Table
                            </button>
                        </div>
                        <button
                            onClick={onClose}
                            className="p-1 transition-colors rounded-md text-neutral-400 hover:text-primary-700 hover:bg-primary-50 focus:outline-none focus-visible:border-primary-700 border border-transparent"
                        >
                            <X size={24} />
                        </button>
                    </div>
                </div>

                {/* Date Range Selection */}
                <div className="p-6 border-b border-neutral-200 bg-neutral-50">
                    <div className="flex flex-col gap-4">
                        {/* Preset Range Buttons */}
                        <div className="flex items-center gap-2">
                            <Calendar size={20} className="text-neutral-600" />
                            <span className="text-sm font-medium text-neutral-700">Date Range:</span>
                            <div className="flex gap-2">
                                <button
                                    onClick={() => handleDateRangeChange('60')}
                                    className={`px-4 py-2 text-sm font-medium rounded-md transition-colors ${dateRange === '60'
                                        ? 'bg-primary-700 text-white'
                                        : 'bg-white text-neutral-700 border border-neutral-300 hover:bg-neutral-50'
                                        }`}
                                >
                                    Last 60 Days
                                </button>
                                <button
                                    onClick={() => handleDateRangeChange('90')}
                                    className={`px-4 py-2 text-sm font-medium rounded-md transition-colors ${dateRange === '90'
                                        ? 'bg-primary-700 text-white'
                                        : 'bg-white text-neutral-700 border border-neutral-300 hover:bg-neutral-50'
                                        }`}
                                >
                                    Last 90 Days
                                </button>
                                <button
                                    onClick={() => handleDateRangeChange('custom')}
                                    className={`px-4 py-2 text-sm font-medium rounded-md transition-colors ${dateRange === 'custom'
                                        ? 'bg-primary-700 text-white'
                                        : 'bg-white text-neutral-700 border border-neutral-300 hover:bg-neutral-50'
                                        }`}
                                >
                                    Custom Range
                                </button>
                            </div>
                        </div>

                        {/* Custom Date Inputs */}
                        {dateRange === 'custom' && (
                            <div className="flex items-center gap-4 ml-8">
                                <div className="flex flex-col">
                                    <label className="text-xs font-medium text-neutral-600 mb-1">Start Date</label>
                                    <input
                                        type="date"
                                        value={customStartDate}
                                        onChange={handleCustomStartDateChange}
                                        max={getTodayDate()}
                                        className="px-3 py-2 text-sm border border-neutral-300 rounded-md focus:outline-none focus:border-primary-500"
                                    />
                                </div>
                                <div className="flex flex-col">
                                    <label className="text-xs font-medium text-neutral-600 mb-1">End Date</label>
                                    <input
                                        type="date"
                                        value={customEndDate}
                                        onChange={handleCustomEndDateChange}
                                        min={customStartDate}
                                        max={getTodayDate()}
                                        disabled={!customStartDate}
                                        className="px-3 py-2 text-sm border border-neutral-300 rounded-md focus:outline-none focus:border-primary-500 disabled:bg-neutral-100 disabled:cursor-not-allowed"
                                    />
                                </div>
                                {customEndDate && customStartDate && (
                                    <div className="text-sm text-neutral-600 self-end pb-2">
                                        {Math.ceil((new Date(customEndDate).getTime() - new Date(customStartDate).getTime()) / (1000 * 60 * 60 * 24))} days selected
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                </div>

                {/* Modal Content */}
                <div className="p-6 overflow-y-auto flex-1">
                    {loading ? (
                        // Chart-shaped placeholder, so the modal keeps its height
                        // instead of collapsing around a spinner.
                        <div className="py-2" aria-busy="true">
                            <Skeleton className="w-48 h-4 mb-4" />
                            <Skeleton className="w-full h-56 mb-4" />
                            <SkeletonText lines={3} />
                        </div>
                    ) : error ? (
                        <div className="text-center py-8">
                            <p className="text-danger-500">{error}</p>
                        </div>
                    ) : data.length === 0 ? (
                        <div className="text-center py-8">
                            <p className="text-neutral-500">No historical data available for the selected date range</p>
                        </div>
                    ) : (
                        <div className="space-y-4">
                            {viewMode === 'chart' ? (
                                <div className="w-full" style={{ minHeight: '450px' }}>
                                    <Chart
                                        options={chartOptions}
                                        series={chartSeries}
                                        type="line"
                                        height={400}
                                    />
                                </div>
                            ) : (
                                <div className="overflow-x-auto">
                                    <table className="min-w-full bg-white border border-neutral-200">
                                        <thead className="bg-neutral-50">
                                            <tr>
                                                <th className="px-4 py-3 text-left text-sm font-semibold text-neutral-700">Date</th>
                                                <th className="px-4 py-3 text-left text-sm font-semibold text-neutral-700">Value</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {hasPlots ? (
                                                prepareTableData().map((item, index) => {
                                                    // Calculate max value from all song versions for this date
                                                    const maxValue = item.plots?.length > 0
                                                        ? Math.max(...item.plots.map((plot: any) => plot.value || 0))
                                                        : 'N/A'

                                                    return (
                                                        <tr key={index} className="border-b border-neutral-100">
                                                            <td className="px-4 py-3 text-sm text-black">
                                                                {item.date ? new Date(item.date).toLocaleDateString() : 'N/A'}
                                                            </td>
                                                            <td className="px-4 py-3 text-sm text-black">
                                                                {maxValue}
                                                            </td>
                                                        </tr>
                                                    )
                                                })
                                            ) : (
                                                prepareTableData().map((item: any, index: number) => (
                                                    <tr key={index} className="border-b border-neutral-100">
                                                        <td className="px-4 py-3 text-sm text-black">
                                                            {item.date ? new Date(item.date).toLocaleDateString() : 'N/A'}
                                                        </td>
                                                        <td className="px-4 py-3 text-sm text-black">
                                                            {item.value ?? 'N/A'}
                                                        </td>
                                                    </tr>
                                                ))
                                            )}
                                        </tbody>
                                    </table>
                                </div>
                            )}
                        </div>
                    )}
                </div>

                {/* Modal Footer */}
                <div className="flex justify-end p-6 border-t border-neutral-200 bg-neutral-50">
                    <Button
                        variant="neutral"
                        onClick={onClose}
                    >
                        Close
                    </Button>
                </div>
            </div>
        </div>
    )
}

export default HistoricalTrendModal